import React, { useState, useEffect, useRef } from 'react';
import PublicPost from './PublicPost';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Spinner,
  Center,
  Text,
  Button,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';
import { useData } from '../../data';

const PublicPosts = () => {
  const [publicFeeds, setPublicFeeds] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const postRefs = useRef({});
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const _postId = searchParams.get('id');

  const {
    data: feedData,
    isLoading: feedDataIsLoading,
    refetch: refetchFeed,
  } = useData(`public/posts?postId=${_postId}`, 10000);

  useEffect(() => {
    if (feedData) {
      setPublicFeeds(feedData?.data?.posts || []);
    }
  }, [feedData]);

  useEffect(() => {
    if (_postId && publicFeeds.length > 0) {
      // Small delay to ensure DOM is rendered
      const timer = setTimeout(() => {
        const targetPost = postRefs.current[_postId];
        if (targetPost) {
          targetPost.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [_postId, publicFeeds]);

  // Show login prompt after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoginPrompt(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleLogin = () => {
    const redirectPath = 'post' + location.search;
    sessionStorage.setItem('_redirectPath', redirectPath);
    navigate('/login');
  };

  const handleRegister = () => {
    navigate('/signUp');
  };

  // Handle loading state
  if (feedDataIsLoading) {
    return (
      <Box className="w-full h-full">
        <Center py={8}>
          <Box textAlign="center">
            <Spinner size="lg" color="blue.500" />
            <Text mt={4} color="gray.600">
              Loading posts...
            </Text>
          </Box>
        </Center>
      </Box>
    );
  }

  // Handle empty state
  if (!feedDataIsLoading && publicFeeds.length === 0) {
    return (
      <Box className="w-full h-full">
        <Center py={8}>
          <Text color="gray.600" fontSize="lg">
            No posts found
          </Text>
        </Center>
      </Box>
    );
  }

  return (
    <Box className="flex flex-col h-screen bg-gray-100 gap-2 w-full">
      <Box
        className={`pt-10 px-5 md:px-10 hide-scroll max-h-[95vh] overflow-x-auto min-h-[94vh] w-full flex justify-center`}
      >
        <Box className="w-full flex flex-col gap-5 max-w-2xl">
          {publicFeeds?.map((post) => {
            return (
              <Box
                key={post.id}
                ref={(el) => (postRefs.current[post.id] = el)}
                className="w-full bg-white rounded-lg shadow-sm"
              >
                <PublicPost
                  post={post}
                  refetchPost={() => {
                    refetchFeed();
                  }}
                  postId={null}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      <Modal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        isCentered
        size="md"
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <ModalContent mx={4} borderRadius="xl" boxShadow="2xl">
          <ModalHeader
            textAlign="center"
            fontSize="xl"
            fontWeight="bold"
            color="gray.800"
            pb={2}
          >
            Want to comment?
          </ModalHeader>
          <ModalCloseButton
            size="lg"
            color="gray.500"
            _hover={{ color: 'gray.700' }}
          />
          <ModalBody textAlign="center" py={6}>
            <Text color="gray.600" fontSize="md" lineHeight="1.6">
              Join the conversation! Log in or create an account to comment on
              posts and engage with the community.
            </Text>
          </ModalBody>
          <ModalFooter justifyContent="center" gap={3} pt={2} pb={6}>
            <Button
              variant="outline"
              size="md"
              onClick={() => setShowLoginPrompt(false)}
              _hover={{ bg: 'gray.50' }}
            >
              Maybe Later
            </Button>
            <Button
              colorScheme="blue"
              size="md"
              onClick={handleLogin}
              _hover={{ transform: 'translateY(-1px)' }}
              transition="all 0.2s"
            >
              Log In
            </Button>
            <Button
              colorScheme="green"
              size="md"
              onClick={handleRegister}
              _hover={{ transform: 'translateY(-1px)' }}
              transition="all 0.2s"
            >
              Create Account
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PublicPosts;
